import {
  campaignApiGet,
  campaignApiGetBlob,
  campaignApiPatch,
} from '@/lib/api/campaign-api-client';
import { ModuleName } from '@/constant/modules.constants';
import { uploadPublicFile } from '@/lib/api/services/storage.service';
import type {
  BrandingSettings,
  BrandingSettingsApi,
  LegalFooterSettings,
  LegalFooterSettingsApi,
  SettingsApiEnvelope,
  UpdateBrandingSettingsInput,
} from '../types/compliance-settings.types';

export const getBrandingLogoBlob = (logoUrl: string): Promise<Blob> => campaignApiGetBlob(logoUrl);

const dataFrom = <T>(response: SettingsApiEnvelope<T>, fallback: string): T => {
  if (!response || response.success === false || response.data == null) {
    throw new Error(response?.message || response?.errors?.join(', ') || fallback);
  }
  return response.data;
};

const mapBranding = (data: BrandingSettingsApi): BrandingSettings => ({
  displayName: data.displayName ?? '',
  resolvedName: data.resolvedName ?? '',
  logoMediaId: data.logoMediaId ?? '',
  logoUrl: data.logoUrl ?? '',
  accentColor: data.accentColor ?? '',
  defaultLocale: data.defaultLocale ?? 'en',
  enabledLocales: data.enabledLocales?.length ? data.enabledLocales : ['de', 'fr', 'it', 'en'],
  unsubscribedRedirectUrl: data.unsubscribedRedirectUrl ?? '',
  showPoweredBy: data.showPoweredBy ?? true,
});

export const getBrandingSettings = async (): Promise<BrandingSettings> => {
  const response =
    await campaignApiGet<SettingsApiEnvelope<BrandingSettingsApi>>('/v1/settings/branding');
  return mapBranding(dataFrom(response, 'Failed to load branding settings'));
};

export const updateBrandingSettings = async (
  input: UpdateBrandingSettingsInput
): Promise<BrandingSettings> => {
  const response = await campaignApiPatch<SettingsApiEnvelope<BrandingSettingsApi>>(
    '/v1/settings/branding',
    input
  );
  return mapBranding(dataFrom(response, 'Failed to save branding settings'));
};

export const uploadBrandingLogo = async (file: File): Promise<{ itemId: string; url: string }> => {
  const { fileId, fileUrl } = await uploadPublicFile(file, ModuleName.DefaultCloud);
  return { itemId: fileId, url: fileUrl };
};

const mapFooter = (data: LegalFooterSettingsApi): LegalFooterSettings => {
  const companyName = data.companyName ?? data.legalName ?? '';
  const addressLine1 = data.addressLine1 ?? data.postalAddress ?? '';
  return {
    companyName,
    addressLine1,
    addressLine2: data.addressLine2 ?? '',
    postalCode: data.postalCode ?? '',
    city: data.city ?? '',
    country: data.country ?? '',
    extraText: data.extraText ?? '',
    includeUnsubscribeLink: data.includeUnsubscribeLink ?? true,
    includePreferencesLink: data.includePreferencesLink ?? false,
    legalName: companyName,
    postalAddress: addressLine1,
  };
};

export const getLegalFooterSettings = async (): Promise<LegalFooterSettings> => {
  const response =
    await campaignApiGet<SettingsApiEnvelope<LegalFooterSettingsApi>>('/v1/settings/footer');
  return mapFooter(dataFrom(response, 'Failed to load legal footer'));
};

export const updateLegalFooterSettings = async (
  input: Partial<LegalFooterSettings>
): Promise<LegalFooterSettings> => {
  const payload: Record<string, unknown> = {};
  if (input.companyName !== undefined || input.legalName !== undefined) {
    payload.companyName = (input.companyName ?? input.legalName ?? '').trim();
  }
  if (input.addressLine1 !== undefined || input.postalAddress !== undefined) {
    payload.addressLine1 = (input.addressLine1 ?? input.postalAddress ?? '').trim();
  }
  if (input.addressLine2 !== undefined) payload.addressLine2 = input.addressLine2.trim();
  if (input.postalCode !== undefined) payload.postalCode = input.postalCode.trim();
  if (input.city !== undefined) payload.city = input.city.trim();
  if (input.country !== undefined) payload.country = input.country.trim();
  if (input.extraText !== undefined) payload.extraText = input.extraText.trim();
  if (input.includeUnsubscribeLink !== undefined) payload.includeUnsubscribeLink = true;
  if (input.includePreferencesLink !== undefined) {
    payload.includePreferencesLink = Boolean(input.includePreferencesLink);
  }

  const response = await campaignApiPatch<SettingsApiEnvelope<LegalFooterSettingsApi>>(
    '/v1/settings/footer',
    payload
  );
  return mapFooter(dataFrom(response, 'Failed to save legal footer'));
};
